import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ImageUploader from './ImageUploader';
import { getSettings, updateSettings } from '../../services/settings';
import { getImageUrl } from '../../services/listings';

const DEFAULT_NAV_LINKS = [
  {
    label: 'Buyer Questionnaire',
    url: 'https://i8grr76tpqp.typeform.com/to/c7JWrqnv?typeform-source=linktr.ee',
    isExternal: true,
    order: 0,
    variant: 'text',
  },
  {
    label: 'Meet Kaden',
    url: 'https://beerhometeam.hifello.com/p/kaden.cowles',
    isExternal: true,
    order: 1,
    variant: 'text',
  },
  {
    label: 'Seller Questionnaire',
    url: 'https://i8grr76tpqp.typeform.com/to/BlERdHqS',
    isExternal: true,
    order: 2,
    variant: 'text',
  },
  {
    label: 'Bookings',
    url: 'https://krispvacations.guestybookings.com/',
    isExternal: true,
    order: 3,
    variant: 'text',
  },
  {
    label: 'About',
    url: '/page-about',
    isExternal: false,
    order: 4,
    variant: 'text',
  },
  {
    label: 'Contact Us',
    url: '/page-contact',
    isExternal: false,
    order: 5,
    variant: 'contained',
  },
];

const CurrentImage = ({ src, alt }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
      Currently in use:
    </Typography>
    <Box
      component="img"
      src={src}
      alt={alt}
      sx={{
        maxHeight: 120,
        maxWidth: '100%',
        objectFit: 'contain',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'grey.300',
        bgcolor: '#fafafa',
        p: 1,
      }}
    />
  </Box>
);

const NavLinkDisplayRow = ({ link, index, total, onEdit, onDelete, onMoveUp, onMoveDown }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      px: 2,
      py: 1.5,
      borderBottom: index < total - 1 ? '1px solid' : 'none',
      borderColor: 'grey.200',
    }}
  >
    <Box sx={{ display: 'flex', flexDirection: 'column', mr: 0.5 }}>
      <IconButton size="small" onClick={onMoveUp} disabled={index === 0}>
        <ArrowUpwardIcon fontSize="small" />
      </IconButton>
      <IconButton size="small" onClick={onMoveDown} disabled={index === total - 1}>
        <ArrowDownwardIcon fontSize="small" />
      </IconButton>
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="body1" sx={{ fontWeight: 500 }}>
        {link.label}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      >
        {link.url}
      </Typography>
    </Box>
    <Typography
      variant="caption"
      sx={{
        px: 1,
        py: 0.25,
        borderRadius: 1,
        bgcolor: link.isExternal ? 'info.light' : 'success.light',
        color: link.isExternal ? 'info.dark' : 'success.dark',
        whiteSpace: 'nowrap',
        fontWeight: 500,
      }}
    >
      {link.isExternal ? 'External' : 'Internal'}
    </Typography>
    <Typography
      variant="caption"
      sx={{
        px: 1,
        py: 0.25,
        borderRadius: 1,
        bgcolor: link.variant === 'contained' ? 'primary.light' : 'grey.200',
        color: link.variant === 'contained' ? 'primary.dark' : 'text.secondary',
        whiteSpace: 'nowrap',
        fontWeight: 500,
      }}
    >
      {link.variant === 'contained' ? 'Button' : 'Text'}
    </Typography>
    <IconButton size="small" onClick={onEdit}>
      <EditIcon fontSize="small" />
    </IconButton>
    <IconButton color="error" size="small" onClick={onDelete}>
      <DeleteIcon fontSize="small" />
    </IconButton>
  </Box>
);

const NavLinkEditRow = ({ link, onChange, onSave, onCancel }) => (
  <Box sx={{ p: 2, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'grey.200' }}>
    <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
      <TextField
        label="Label"
        value={link.label}
        onChange={(e) => onChange({ ...link, label: e.target.value })}
        size="small"
        sx={{ minWidth: 120, flex: 1 }}
      />
      <TextField
        label="URL"
        value={link.url}
        onChange={(e) => onChange({ ...link, url: e.target.value })}
        size="small"
        sx={{ minWidth: 200, flex: 2 }}
      />
    </Box>
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', justifyContent: 'space-between' }}>
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 110 }}>
          <InputLabel>Style</InputLabel>
          <Select
            value={link.variant}
            label="Style"
            onChange={(e) => onChange({ ...link, variant: e.target.value })}
          >
            <MenuItem value="text">Text</MenuItem>
            <MenuItem value="contained">Button</MenuItem>
          </Select>
        </FormControl>
        <FormControlLabel
          control={
            <Switch
              checked={link.isExternal}
              onChange={(e) => onChange({ ...link, isExternal: e.target.checked })}
              size="small"
            />
          }
          label="External"
          sx={{ mr: 0 }}
        />
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button size="small" onClick={onCancel}>Cancel</Button>
        <Button size="small" variant="contained" onClick={onSave} disabled={!link.label || !link.url}>
          Done
        </Button>
      </Box>
    </Box>
  </Box>
);

const SiteSettings = () => {
  const [logo, setLogo] = useState([]);
  const [profilePhoto, setProfilePhoto] = useState([]);
  const [navLinks, setNavLinks] = useState(DEFAULT_NAV_LINKS);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settings = await getSettings();
        if (settings.logo) setLogo([settings.logo]);
        if (settings.profilePhoto) setProfilePhoto([settings.profilePhoto]);
        if (settings.navLinks && settings.navLinks.length > 0) {
          setNavLinks(settings.navLinks);
        }
      } catch (error) {
        console.error('Error loading settings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleAddLink = () => {
    const newLink = {
      label: '',
      url: '',
      isExternal: true,
      order: navLinks.length,
      variant: 'text',
    };
    setNavLinks((prev) => [...prev, newLink]);
    setEditingIndex(navLinks.length);
    setEditDraft(newLink);
  };

  const handleStartEdit = (index) => {
    setEditingIndex(index);
    setEditDraft({ ...navLinks[index] });
  };

  const handleSaveEdit = () => {
    setNavLinks((prev) => prev.map((link, i) => (i === editingIndex ? editDraft : link)));
    setEditingIndex(null);
    setEditDraft(null);
  };

  const handleCancelEdit = () => {
    // If it was a new blank link that was never saved, remove it
    if (editingIndex !== null && !navLinks[editingIndex].label && !navLinks[editingIndex].url) {
      setNavLinks((prev) => prev.filter((_, i) => i !== editingIndex));
    }
    setEditingIndex(null);
    setEditDraft(null);
  };

  const handleDeleteLink = (index) => {
    if (editingIndex === index) {
      setEditingIndex(null);
      setEditDraft(null);
    }
    setNavLinks((prev) =>
      prev.filter((_, i) => i !== index).map((link, i) => ({ ...link, order: i }))
    );
  };

  const handleMoveLink = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= navLinks.length) return;
    const updated = [...navLinks];
    const temp = updated[index];
    updated[index] = updated[newIndex];
    updated[newIndex] = temp;
    setNavLinks(updated.map((link, i) => ({ ...link, order: i })));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await updateSettings({
        logo: logo.length > 0 ? logo[0] : null,
        profilePhoto: profilePhoto.length > 0 ? profilePhoto[0] : null,
        navLinks: navLinks.map((link, i) => ({
          label: link.label,
          url: link.url,
          isExternal: link.isExternal,
          order: i,
          variant: link.variant,
        })),
      });
      setMessage({ type: 'success', text: 'Settings saved successfully.' });
    } catch (error) {
      console.error('Error saving settings:', error);
      const detail = error?.response?.data?.details || error?.response?.data?.error || '';
      setMessage({
        type: 'error',
        text: `Error saving settings. ${detail ? detail : 'Please try again.'}`,
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 500 }}>
          Site Logo
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Displayed in the navigation bar. Falls back to the default logo if not set.
          For best performance, upload images in WebP format.
        </Typography>
        <CurrentImage
          src={logo.length > 0 ? getImageUrl(logo[0]) : '/krisp.webp'}
          alt="Current logo"
        />
        <ImageUploader
          value={logo}
          onChange={setLogo}
          multiple={false}
          maxFiles={1}
        />
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 500 }}>
          Profile Photo
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Displayed on the About sections. Falls back to the default photo if not set.
          For best performance, upload images in WebP format.
        </Typography>
        <CurrentImage
          src={profilePhoto.length > 0 ? getImageUrl(profilePhoto[0]) : '/kaden.webp'}
          alt="Current profile photo"
        />
        <ImageUploader
          value={profilePhoto}
          onChange={setProfilePhoto}
          multiple={false}
          maxFiles={1}
        />
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            Navigation Links
          </Typography>
          <Button startIcon={<AddIcon />} onClick={handleAddLink} size="small">
            Add Link
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Manage the links displayed in the navigation bar. Use arrows to reorder.
          If no links are configured, the default links will be shown.
        </Typography>
        {navLinks.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            No navigation links configured. Click "Add Link" to create one.
          </Typography>
        ) : (
          <Box sx={{ border: '1px solid', borderColor: 'grey.200', borderRadius: 1 }}>
            {navLinks.map((link, index) =>
              editingIndex === index ? (
                <NavLinkEditRow
                  key={index}
                  link={editDraft}
                  onChange={setEditDraft}
                  onSave={handleSaveEdit}
                  onCancel={handleCancelEdit}
                />
              ) : (
                <NavLinkDisplayRow
                  key={index}
                  link={link}
                  index={index}
                  total={navLinks.length}
                  onEdit={() => handleStartEdit(index)}
                  onDelete={() => handleDeleteLink(index)}
                  onMoveUp={() => handleMoveLink(index, -1)}
                  onMoveDown={() => handleMoveLink(index, 1)}
                />
              )
            )}
          </Box>
        )}
      </Paper>

      <Button
        variant="contained"
        color="primary"
        onClick={handleSave}
        disabled={saving}
        size="large"
      >
        {saving ? 'Saving...' : 'Save Settings'}
      </Button>
    </Box>
  );
};

export default SiteSettings;
